import React, { useEffect, useMemo, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
} from "@mui/material";
import { SimpleTextInput } from "components/FormTextInput";
import { SimpleSelectInput } from "components/FormSelectInput";
import SearchableInput from "components/SearchableInput";
import { getPlatforms } from "services/platformService";
import { getGamesList } from "services/gameService";
import { quickCreateOperator } from "services/operatorService";

const DEFAULT_GAME_ALIAS = "Playbook-Fusion-Football";

interface QuickOperatorAddModalProps {
  open: boolean;
  onClose: () => void;
  onCreated: (operator: any) => void;
}

const QuickOperatorAddModal = ({
  open,
  onClose,
  onCreated,
}: QuickOperatorAddModalProps) => {
  const [platforms, setPlatforms] = useState<any[]>([]);
  const [games, setGames] = useState<any[]>([]);
  const [platform, setPlatform] = useState("");
  const [operatorName, setOperatorName] = useState("");
  const [externalOpId, setExternalOpId] = useState("");
  const [game, setGame] = useState("");
  const [loading, setLoading] = useState(false);
  const [optionsLoading, setOptionsLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!open) return;

    let cancelled = false;

    setPlatform("");
    setOperatorName("");
    setExternalOpId("");
    setGame("");
    setError("");
    setOptionsLoading(true);

    (async () => {
      try {
        const [platformsResp, gamesResp] = await Promise.all([
          getPlatforms({ skipPagination: true }),
          getGamesList({ skipPagination: true }),
        ]);

        if (cancelled) return;

        const fetchedPlatforms = platformsResp?.data?.platforms || [];
        setPlatforms(Array.isArray(fetchedPlatforms) ? fetchedPlatforms : []);

        const fetchedGames =
          gamesResp?.data?.platforms || gamesResp?.data?.data || gamesResp?.data || [];
        const safeGames = Array.isArray(fetchedGames) ? fetchedGames : [];
        setGames(safeGames);

        const defaultGame = safeGames.find(
          (g: any) => g.gameAlias === DEFAULT_GAME_ALIAS,
        );
        if (defaultGame) setGame(defaultGame._id);
      } catch (err: any) {
        if (!cancelled) setError(err.message || "Failed to load platforms/games");
      } finally {
        if (!cancelled) setOptionsLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [open]);

  const platformOptions = useMemo(
    () =>
      platforms
        .map((p) => ({
          id: p._id,
          label: p.platformName,
          disabled: !p.extraData?.defaultOperatorCreateInput,
          subLabel: !p.extraData?.defaultOperatorCreateInput
            ? "No default template configured"
            : undefined,
        }))
        .sort((a, b) => {
          if (a.disabled !== b.disabled) return a.disabled ? 1 : -1;
          return String(a.label).localeCompare(String(b.label));
        }),
    [platforms],
  );

  const gameOptions = useMemo(
    () =>
      games.map((g) => ({
        id: g._id,
        label: `${g.gameAlias} (${g.gameName})`,
      })),
    [games],
  );

  const selectedPlatform = platforms.find((p) => p._id === platform);
  const platformEligible = !!selectedPlatform?.extraData?.defaultOperatorCreateInput;
  const selectedPlatformOption = platformOptions.find((o) => o.id === platform) ?? null;

  const canSubmit =
    !!platform && platformEligible && !!operatorName && !!externalOpId && !!game;

  const handleSubmit = async () => {
    setLoading(true);
    setError("");
    try {
      const resp = await quickCreateOperator({
        platform,
        operatorName,
        externalOpId,
        game,
      });
      onCreated(resp?.data);
    } catch (err: any) {
      setError(err.message || "Failed to create operator");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>Quick Add Operator</DialogTitle>
      <DialogContent>
        <Box display="flex" flexDirection="column" gap={2} mt={1}>
          <SearchableInput
            label="Platform"
            name="platform"
            placeholder="Search platform"
            selected={selectedPlatformOption}
            options={platformOptions}
            loading={optionsLoading}
            onOptionSelect={(e: any) => setPlatform(e.target.value?.id ?? "")}
            getOptionDisabled={(option: any) => !!option.disabled}
            renderOption={(props: any, option: any) => {
              const { key, ...otherProps } = props;
              return (
                <li key={option.id ?? key} {...otherProps}>
                  <Box display="flex" flexDirection="column">
                    <span>{option.label}</span>
                    {option.subLabel && (
                      <span
                        style={{ fontSize: 11, fontStyle: "italic", color: "gray" }}
                      >
                        {option.subLabel}
                      </span>
                    )}
                  </Box>
                </li>
              );
            }}
          />

          <SimpleTextInput
            label="Operator Name"
            name="operatorName"
            value={operatorName}
            onChange={(e: any) => setOperatorName(e.target.value)}
          />

          <SimpleTextInput
            label="Casino ID (External ID)"
            name="externalOpId"
            value={externalOpId}
            onChange={(e: any) => setExternalOpId(e.target.value)}
            placeholder="Enter external casino identifier"
          />

          <SimpleSelectInput
            label="Game"
            name="game"
            value={game}
            options={gameOptions}
            onChange={(e: any) => setGame(e.target.value)}
            placeholdertext={optionsLoading ? "Loading..." : undefined}
          />

          {error && (
            <Typography color="error" variant="body2">
              {error}
            </Typography>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} variant="outlined">
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={!canSubmit || loading || optionsLoading}
        >
          {loading ? "Creating..." : "Create"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default QuickOperatorAddModal;
